import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Menu, X, Info, Star, ShieldCheck, Users2, Award } from "lucide-react";
import { BIZ, SERVICE_GROUPS, HISTORY, FOUNDER, VALUES, TEAM_NOTE, REPUTATION } from "./hicoJandaData";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import AccountingInquiryForm from "@/components/AccountingInquiryForm";

const NAVY = "#1E3A5F";
const GOLD = "#C9962C";
const CREAM = "#F8F5EE";
const INK = "#232323";
const SLATE = "#565048";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function HicoJandaSite() {
  const [open, setOpen] = useState(false);
  const links = [["Values", "#values"], ["Services", "#services"], ["Our Firm", "#firm"], ["Contact", "#contact"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Inter', sans-serif" }} data-testid="hicojanda-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@600;700&family=Inter:wght@400;500;600;700&display=swap'); .hj-serif{font-family:'Merriweather',serif;}`}</style>

      <div data-testid="accounting-disclaimer" className="bg-black text-white text-center text-xs py-2.5 px-4">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: GOLD }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 shadow-sm" style={{ background: NAVY }}>
        <nav className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="hicojanda-logo" className="hj-serif text-white text-base">Hico, Janda &amp; Co., CPAs</a>
          <div className="hidden md:flex items-center gap-7">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium text-white/75 hover:text-white transition-colors">{l}</a>)}
          </div>
          <a href={`tel:${BIZ.phone}`} data-testid="hicojanda-header-cta" className="hidden sm:inline-flex rounded-md px-5 py-2 text-sm font-semibold" style={{ background: GOLD, color: NAVY }}>Call Now</a>
          <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="hicojanda-menu-toggle" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1" style={{ background: NAVY }}>{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-white/85">{l}</a>)}</div>}
      </header>

      <section className="py-12 sm:py-16" style={{ background: "#E7E0D0" }} data-testid="hicojanda-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-[0.34fr_1fr]">
          <aside className="p-8 sm:p-10 flex flex-col justify-between" style={{ background: NAVY, color: "white" }}>
            <Award size={34} style={{ color: GOLD }} />
            <div className="mt-14 md:mt-0">
              <p className="text-xs uppercase tracking-[0.2em] text-white/55">Bay, Laguna</p>
              <p className="hj-serif text-xl mt-3">Hico, Janda &amp; Co., CPAs</p>
            </div>
          </aside>
          <div className="p-8 sm:p-12 lg:p-16 bg-white border-t-8" style={{ borderColor: GOLD }}>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: GOLD }}>Hico, Janda &amp; Co., CPAs</p>
            <motion.h1 {...fadeUp} className="mt-5 hj-serif text-3xl sm:text-5xl leading-[1.2]" style={{ color: NAVY }}>{BIZ.tagline}</motion.h1>
            <div className="mt-7 flex flex-wrap gap-3">{VALUES.map((value) => <span key={value} className="px-3 py-2 text-xs uppercase tracking-wide border" style={{ borderColor: "#D5C8AC", color: SLATE }}>{value}</span>)}</div>
            <motion.div {...fadeUp} className="mt-8 flex flex-wrap gap-4">
              <a href="#contact" data-testid="hicojanda-hero-cta" className="px-7 py-3.5 text-sm font-semibold" style={{ background: GOLD, color: NAVY }}>Ask About Your Books</a>
              <a href={`tel:${BIZ.phone}`} data-testid="hicojanda-hero-call" className="px-7 py-3.5 text-sm font-semibold border" style={{ color: NAVY, borderColor: "#C8B88F" }}>Call {BIZ.phoneDisplay}</a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES — three full-height pillars, promoted above services as the page's spine */}
      <section id="values" className="grid sm:grid-cols-3">
        {VALUES.map((v, i) => (
          <div key={v} className="px-8 py-14 sm:py-20 text-center" style={{ background: i === 0 ? NAVY : i === 1 ? "#2A4A73" : "#375D8C" }}>
            <Award size={26} style={{ color: GOLD }} className="mx-auto" />
            <p className="mt-4 hj-serif text-2xl text-white">{v}</p>
          </div>
        ))}
      </section>

      {/* SERVICES — real architecture, grouped */}
      <section id="services" className="py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="hj-serif text-2xl sm:text-3xl text-center mb-10" style={{ color: NAVY }}>What We Handle</motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {SERVICE_GROUPS.map((g) => (
              <motion.div key={g.title} {...fadeUp} className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="hj-serif text-lg mb-3" style={{ color: GOLD }}>{g.title}</h3>
                <div className="flex flex-wrap gap-2">
                  {g.areas.map((a) => <span key={a} className="text-xs font-medium rounded-full px-3.5 py-1.5" style={{ background: CREAM, color: INK }}>{a}</span>)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* OUR FIRM — history + founder, careful framing */}
      <section id="firm" className="py-16" style={{ background: "#EFE8D8" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="hj-serif text-2xl sm:text-3xl text-center mb-8" style={{ color: NAVY }}>Our Firm</motion.h2>
          <motion.div {...fadeUp} className="bg-white rounded-lg p-6 shadow-sm mb-5">
            <h3 className="font-bold text-sm uppercase tracking-wide mb-2" style={{ color: GOLD }}>Founder Background</h3>
            <p className="text-sm" style={{ color: SLATE }}>{FOUNDER}</p>
          </motion.div>
          <motion.div {...fadeUp} className="flex items-start gap-2.5 rounded-lg p-4 text-xs bg-white mb-5">
            <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: GOLD }} />
            {HISTORY}
          </motion.div>
          <motion.div {...fadeUp} className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="font-bold text-sm uppercase tracking-wide mb-2 flex items-center gap-2" style={{ color: GOLD }}><Users2 size={16} /> Current Team</h3>
            <p className="text-sm" style={{ color: SLATE }}>{TEAM_NOTE}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-12" style={{ background: NAVY }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 mb-2">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill={GOLD} style={{ color: GOLD }} />)}
          </div>
          <p className="text-lg font-bold text-white">{REPUTATION.rating}★ · {REPUTATION.reviewCountLabel}</p>
          <p className="mt-1.5 text-xs text-white/60">{REPUTATION.note}</p>
        </div>
      </section>

      <section id="contact" className="py-16" style={{ background: "#EFE8D8" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10">
          <motion.div {...fadeUp}>
            <h2 className="hj-serif text-2xl mb-6" style={{ color: NAVY }}>Office</h2>
            <ul className="space-y-4 text-sm mb-6">
              <li className="flex items-start gap-3"><MapPin size={18} style={{ color: GOLD }} className="mt-0.5 shrink-0" />{BIZ.address}</li>
              <li className="flex items-start gap-3"><Phone size={18} style={{ color: GOLD }} className="mt-0.5 shrink-0" /><a href={`tel:${BIZ.phone}`} data-testid="hicojanda-phone-link" className="hover:underline">{BIZ.phoneDisplay}</a></li>
            </ul>
            <div className="rounded-lg overflow-hidden shadow-sm min-h-[220px]">
              <iframe title="Hico, Janda & Co., CPAs map" src={BIZ.mapEmbed} className="w-full h-full min-h-[220px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <AccountingInquiryForm
              testidPrefix="hicojanda"
              businessName={BIZ.name}
              accent={NAVY}
              accentText="#FFFFFF"
              phone={BIZ.phone}
              phoneDisplay={BIZ.phoneDisplay}
            />
          </motion.div>
        </div>
      </section>

      <footer className="py-8" style={{ background: NAVY }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <p className="hj-serif text-white">Hico, Janda &amp; Co., CPAs</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm current details before visiting.</p>
        </div>
      </footer>

      <ConceptCTAPill slug="hico-janda-cpas-concept" />
    </div>
  );
}
